import { type AxiosInstance } from "axios";
import { useSWRConfig } from "swr";
import type { PropertySummaryEditForm } from "@/features/propertySummary/types/schemas";
import { format } from "date-fns";

interface SaveArgs {
  apiClient: AxiosInstance;
  sheetId: number;
  values: PropertySummaryEditForm;
}

// 送信するHTTPマルチパートのデータを生成する
const buildFormData = (values: PropertySummaryEditForm): FormData => {
  const fd = new FormData();

  fd.append("name", values.buildingName);
  fd.append("price", values.price ? values.price.toString() : "");

  fd.append("address", values.address ?? "");
  fd.append("chiban", values.chiban ?? "");

  fd.append("access", values.access ?? "");

  fd.append("land_area", values.landArea ?? "");
  fd.append("land_category", values.landCategory ?? "");
  fd.append("area_use_purpose", values.areaUsePurpose ?? "");
  fd.append("ordinance", values.ordinance ?? "");
  fd.append("building_rate", values.buildingCoverageRatio ?? "");
  fd.append("volume_rate", values.volumeRate ?? "");

  fd.append("building_structure", values.buildingVariant ?? "");
  fd.append("building_type", values.buildingType ?? "");
  fd.append("building_total_floor_area_exclusive", values.area ?? "");
  fd.append(
    "building_new_construction_date",
    values.buildDate ? format(values.buildDate, "yyyy-MM-dd") : ""
  );
  fd.append("balcony_area", values.balconyArea ?? "");
  fd.append("units_and_layout", values.buildingCount ?? "");

  fd.append("yield_rate", values.yield ?? "");

  fd.append("remarks", values.remarks ?? "");
  fd.append("description", values.description ?? "");

  // Map Photos
  if (values.mapPhotos.length > 0) {
    fd.append(
      "map_photo_id",
      values.mapPhotos[0]?.id ? String(values.mapPhotos[0]?.id) : ""
    );
    if (values.mapPhotos[0]?.file instanceof File) {
      fd.append("map_photo_file", values.mapPhotos[0]?.file);
    }
  } else {
    fd.append("map_photo_id", "");
    fd.append("map_photo_file", "");
  }

  fd.append(
    "site_photo_1_id",
    values.sitePhoto_1?.id ? String(values.sitePhoto_1?.id) : ""
  );

  if (values.sitePhoto_1?.file instanceof File) {
    fd.append("site_photo_1_file", values.sitePhoto_1.file);
  }

  fd.append(
    "site_photo_2_id",
    values.sitePhoto_2?.id ? String(values.sitePhoto_2?.id) : ""
  );

  if (values.sitePhoto_2?.file instanceof File) {
    fd.append("site_photo_2_file", values.sitePhoto_2.file);
  }

  fd.append("on_foot", "");

  return fd;
};

/**
 * 物件概要書を編集・更新するためのAPI関数
 * - apiClient: APIリクエストを行うためのAxiosインスタンス
 * - sheetId: 更新対象の物件概要書ID
 * - values: 更新する物件概要書のフォームデータ
 * @returns {Function} 物件概要書の更新を実行する関数を返す
 *
 */
export const EditPropertySummaryAPI = (): ((
  args: SaveArgs
) => Promise<void>) => {
  const { mutate } = useSWRConfig();

  return async ({ apiClient, sheetId, values }: SaveArgs): Promise<void> => {
    const key = `/property-summary/${String(sheetId)}`;
    const fd = buildFormData(values);

    await mutate(
      key,
      async (): Promise<unknown> => {
        await apiClient.put(key, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // After successful PUT, fetch latest detail
        const response = await apiClient.get(key);
        return response.data as unknown;
      },
      { revalidate: false }
    );
  };
};

export default EditPropertySummaryAPI;
